// Centralized API Client for CRM Wialon (Telemetry ERP)

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

async function request<T = any>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = `${API_BASE}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
  const token = localStorage.getItem('crm_wialon_token');

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };

  // Si no es FormData, agregar Content-Type: application/json
  if (!(options.body instanceof FormData) && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(url, {
    ...options,
    headers,
    credentials: 'include', // Para cookies HttpOnly
  });

  if (!res.ok) {
    let errMsg = `Error ${res.status}: ${res.statusText}`;
    try {
      const data = await res.json();
      if (data && data.error) errMsg = data.error;
    } catch {
      // Ignorar error al parsear JSON
    }
    throw new Error(errMsg);
  }

  // Si 204 No Content
  if (res.status === 204) return {} as T;

  return res.json() as Promise<T>;
}

export const api = {
  get: <T = any>(endpoint: string) => request<T>(endpoint, { method: 'GET' }),
  post: <T = any>(endpoint: string, body?: any) =>
    request<T>(endpoint, {
      method: 'POST',
      body: body instanceof FormData ? body : JSON.stringify(body),
    }),
  put: <T = any>(endpoint: string, body?: any) =>
    request<T>(endpoint, {
      method: 'PUT',
      body: body instanceof FormData ? body : JSON.stringify(body),
    }),
  delete: <T = any>(endpoint: string) => request<T>(endpoint, { method: 'DELETE' }),

  auth: {
    login: async (email: string, password: string) => {
      const data = await request<{ ok: boolean; user: any; token: string }>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      if (data.token) {
        localStorage.setItem('crm_wialon_token', data.token);
      }
      return data;
    },
    logout: async () => {
      try {
        await request('/auth/logout', { method: 'POST' });
      } finally {
        localStorage.removeItem('crm_wialon_token');
        localStorage.removeItem('telemetry_erp_session');
      }
    },
    me: () => request<{ user: any }>('/auth/me'),
  },

  clients: {
    list: () => request<any[]>('/clients'),
    get: (id: number | string) => request<any>(`/clients/${id}`),
    create: (data: any) => request<any>('/clients', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number | string, data: any) =>
      request<any>(`/clients/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: number | string) => request<any>(`/clients/${id}`, { method: 'DELETE' }),
  },

  contacts: {
    list: (clientId?: number | string) =>
      request<any[]>(`/contacts${clientId ? `?client_id=${clientId}` : ''}`),
    create: (data: any) => request<any>('/contacts', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number | string, data: any) =>
      request<any>(`/contacts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: number | string) => request<any>(`/contacts/${id}`, { method: 'DELETE' }),
  },

  products: {
    list: () => request<any[]>('/products'),
    get: (id: number | string) => request<any>(`/products/${id}`),
    create: (data: any) => request<any>('/products', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number | string, data: any) =>
      request<any>(`/products/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: number | string) => request<any>(`/products/${id}`, { method: 'DELETE' }),
  },

  subscriptions: {
    list: (params?: { client_id?: number | string; status?: string }) => {
      const q = new URLSearchParams();
      if (params?.client_id) q.set('client_id', String(params.client_id));
      if (params?.status) q.set('status', params.status);
      const str = q.toString();
      return request<any[]>(`/subscriptions${str ? `?${str}` : ''}`);
    },
    get: (id: number | string) => request<any>(`/subscriptions/${id}`),
    create: (data: any) => request<any>('/subscriptions', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number | string, data: any) =>
      request<any>(`/subscriptions/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: number | string) => request<any>(`/subscriptions/${id}`, { method: 'DELETE' }),
  },

  invoices: {
    list: (params?: { client_id?: number | string; status?: string }) => {
      const q = new URLSearchParams();
      if (params?.client_id) q.set('client_id', String(params.client_id));
      if (params?.status) q.set('status', params.status);
      const str = q.toString();
      return request<any[]>(`/invoices${str ? `?${str}` : ''}`);
    },
    get: (id: number | string) => request<any>(`/invoices/${id}`),
    create: (data: any) => request<any>('/invoices', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number | string, data: any) =>
      request<any>(`/invoices/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    registerPayment: (id: number | string, paymentData: any) =>
      request<any>(`/invoices/${id}/payments`, { method: 'POST', body: JSON.stringify(paymentData) }),
  },

  payments: {
    list: (invoiceId?: number | string) =>
      request<any[]>(`/payments${invoiceId ? `?invoice_id=${invoiceId}` : ''}`),
  },

  recurringBilling: {
    run: () => request<{ ok: boolean; result?: any }>('/recurring-billing/run', { method: 'POST' }),
  },

  dashboard: {
    getMetrics: () => request<any>('/dashboard/metrics'),
  },

  mailer: {
    sendInvoiceEmail: (formData: FormData) =>
      request<{ ok: boolean; messageId?: string }>('/send-invoice-email', {
        method: 'POST',
        body: formData,
      }),
  },

  backup: {
    export: () => request<any>('/backup/export'),
  },
};

